import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  ApiError,
  getClimate,
  setAutoClimate,
  setNightMode,
  type ClimateSnapshot,
  type ClimateSwitchState,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';

const POLL_INTERVAL_MS = 15_000;

type SwitchKey = keyof ClimateSnapshot['switches'];

interface PendingSwitch {
  key: SwitchKey;
  label: string;
  on: boolean;
}

const UNAVAILABLE_LABELS: Record<string, string> = {
  co2: 'CO2',
  temperature: 'temperature',
  humidity: 'humidity',
  ac1: 'AC 1',
  ac2: 'AC 2',
  autoClimate: 'Auto Climate',
  nightMode: 'Night Mode',
};

export function ClimatePage() {
  const [climate, setClimate] = useState<ClimateSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null);
  const actionInFlight = useRef(false);
  const actionVersion = useRef(0);

  useEffect(() => {
    let mounted = true;
    let activeRequest: AbortController | null = null;
    let requestVersion = 0;

    const load = async () => {
      // A switch action performs its own read-back. Do not let an overlapping
      // poll replace that observed state with a response sampled before it.
      if (actionInFlight.current) return;

      activeRequest?.abort();
      const controller = new AbortController();
      activeRequest = controller;
      const version = ++requestVersion;
      const actionVersionAtStart = actionVersion.current;
      setLoading(true);

      try {
        const snapshot = await getClimate(controller.signal);
        if (
          mounted &&
          version === requestVersion &&
          actionVersionAtStart === actionVersion.current
        ) {
          setClimate(snapshot);
          setError(null);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (
          mounted &&
          version === requestVersion &&
          actionVersionAtStart === actionVersion.current
        ) {
          // A failed aggregate request must not leave old readings displayed as
          // if they were current. A partial result is still a successful 200.
          setClimate(null);
          setError(messageFor(err, 'Climate readings could not be loaded.'));
        }
      } finally {
        if (mounted && activeRequest === controller) setLoading(false);
      }
    };

    void load();
    const interval = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      activeRequest?.abort();
    };
  }, []);

  const requestSwitchChange = (key: SwitchKey, label: string, current: boolean | null) => {
    if (current === null) return;
    setPendingSwitch({ key, label, on: !current });
  };

  const confirmSwitchChange = async () => {
    if (!pendingSwitch) return;
    const change = pendingSwitch;
    actionInFlight.current = true;
    actionVersion.current += 1;

    try {
      const result: ClimateSwitchState =
        change.key === 'autoClimate'
          ? await setAutoClimate(change.on)
          : await setNightMode(change.on);
      // The API returns the device's post-action read-back. Never echo the
      // requested state: automation or another admin may have chosen otherwise.
      setClimate((current) =>
        current === null
          ? current
          : { ...current, switches: { ...current.switches, [change.key]: result.on } },
      );
      setError(null);
    } catch (err) {
      // There is no optimistic update, so the current value remains the last
      // known-good device state if the action fails.
      setError(messageFor(err, 'Climate policy change failed.'));
    } finally {
      actionInFlight.current = false;
      setPendingSwitch(null);
    }
  };

  if (loading && climate === null && error === null) {
    return <p style={{ ...T.ui, color: PS.textSec }}>Loading climate readings…</p>;
  }

  if (climate === null) {
    return (
      <section style={pageStyle} aria-labelledby="climate-heading">
        <h1 id="climate-heading" style={{ ...T.sectionTitle, margin: 0 }}>Climate</h1>
        <ErrorNotice message={error ?? 'Climate readings could not be loaded.'} />
      </section>
    );
  }

  const unavailable = climate.unavailable
    .map((field) => UNAVAILABLE_LABELS[field] ?? field)
    .join(', ');

  return (
    <section style={pageStyle} aria-labelledby="climate-heading">
      <header>
        <h1 id="climate-heading" style={{ ...T.sectionTitle, margin: 0 }}>Climate</h1>
        <p style={{ ...T.caption, color: PS.textSec, margin: '4px 0 0' }}>
          Hall readings refresh automatically every 15 seconds.
        </p>
      </header>

      {error !== null && <ErrorNotice message={error} />}
      {unavailable && (
        <p style={{ ...T.caption, color: PS.warning, margin: 0 }}>
          Unavailable: {unavailable}
        </p>
      )}

      <div style={readingGrid}>
        <ReadingCard label="CO2" value={formatReading(climate.co2, 'ppm')} />
        <ReadingCard label="Temperature" value={formatReading(climate.temperature, '°C')} />
        <ReadingCard label="Humidity" value={formatReading(climate.humidity, '%')} />
      </div>

      <section aria-labelledby="air-conditioning-heading">
        <h2 id="air-conditioning-heading" style={{ ...T.subheading, margin: '0 0 12px' }}>
          Air conditioning
        </h2>
        <div style={acGrid}>
          {[climate.acs[0] ?? null, climate.acs[1] ?? null].map((ac, index) => (
            <AirConditionerCard key={ac?.id ?? `ac-${index + 1}`} ac={ac} fallbackId={`AC ${index + 1}`} />
          ))}
        </div>
      </section>

      <section aria-labelledby="policies-heading">
        <h2 id="policies-heading" style={{ ...T.subheading, margin: '0 0 12px' }}>
          Automation policies
        </h2>
        <div style={policyGrid}>
          <PolicySwitch
            label="Auto Climate"
            on={climate.switches.autoClimate}
            disabled={pendingSwitch !== null}
            onChange={() =>
              requestSwitchChange('autoClimate', 'Auto Climate', climate.switches.autoClimate)
            }
          />
          <PolicySwitch
            label="Night Mode"
            on={climate.switches.nightMode}
            disabled={pendingSwitch !== null}
            onChange={() => requestSwitchChange('nightMode', 'Night Mode', climate.switches.nightMode)}
          />
        </div>
      </section>

      {loading && <p style={{ ...T.caption, color: PS.textTer, margin: 0 }}>Refreshing…</p>}

      <ConfirmDialog
        open={pendingSwitch !== null}
        title={`Turn ${pendingSwitch?.label ?? ''} ${pendingSwitch?.on ? 'on' : 'off'}?`}
        message={`This requests that the hall controller turn ${pendingSwitch?.label ?? 'this policy'} ${pendingSwitch?.on ? 'on' : 'off'}. The displayed state will follow the controller's read-back.`}
        confirmLabel={`Turn ${pendingSwitch?.on ? 'on' : 'off'}`}
        onConfirm={confirmSwitchChange}
        onCancel={() => setPendingSwitch(null)}
      />
    </section>
  );
}

function messageFor(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

function formatReading(value: number | null, unit: string): string {
  return value === null ? 'no reading' : `${value} ${unit}`;
}

function ReadingCard({ label, value }: { label: string; value: string }) {
  return (
    <article style={cardStyle}>
      <div style={T.label}>{label}</div>
      <div style={{ ...T.cardHeading, color: value === 'no reading' ? PS.textSec : PS.nearBlack }}>
        {value}
      </div>
    </article>
  );
}

function AirConditionerCard({
  ac,
  fallbackId,
}: {
  ac: ClimateSnapshot['acs'][number] | null;
  fallbackId: string;
}) {
  const value = (item: string | number | null, unit = '') =>
    item === null ? 'no reading' : `${item}${unit ? ` ${unit}` : ''}`;

  return (
    <article style={cardStyle}>
      <h3 style={{ ...T.cardHeading, margin: 0 }}>{ac?.id ?? fallbackId}</h3>
      <dl style={detailListStyle}>
        <Detail label="Mode" value={value(ac?.mode ?? null)} />
        <Detail label="Current temperature" value={value(ac?.current ?? null, '°C')} />
        <Detail label="Target temperature" value={value(ac?.target ?? null, '°C')} />
        <Detail label="Fan mode" value={value(ac?.fanMode ?? null)} />
      </dl>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <dt style={T.label}>{label}</dt>
      <dd style={{ ...T.ui, color: value === 'no reading' ? PS.textSec : PS.nearBlack, margin: 0 }}>
        {value}
      </dd>
    </div>
  );
}

function PolicySwitch({
  label,
  on,
  disabled,
  onChange,
}: {
  label: string;
  on: boolean | null;
  disabled: boolean;
  onChange: () => void;
}) {
  const status = on === null ? 'no reading' : on ? 'on' : 'off';
  const unavailable = on === null;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on === true}
      aria-label={`${label}: ${status}`}
      disabled={disabled || unavailable}
      onClick={onChange}
      style={{
        ...cardStyle,
        cursor: disabled || unavailable ? 'not-allowed' : 'pointer',
        opacity: disabled || unavailable ? 0.65 : 1,
        textAlign: 'left',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span style={T.subheading}>{label}</span>
      <span
        style={{
          ...T.ui,
          color: on ? PS.success : PS.textSec,
          textTransform: 'uppercase',
        }}
      >
        {status}
      </span>
    </button>
  );
}

function ErrorNotice({ message }: { message: string }) {
  return (
    <p
      role="alert"
      style={{
        ...T.ui,
        color: PS.errorText,
        background: PS.errorBg,
        border: `1px solid ${PS.errorBorder}`,
        borderRadius: 3,
        padding: '12px 16px',
        margin: 0,
      }}
    >
      {message}
    </p>
  );
}

const pageStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 20,
  maxWidth: 920,
};

const readingGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};

const acGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 12,
};

const policyGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 12,
};

const cardStyle = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  boxShadow: `0 1px 2px ${PS.borderLight}`,
  padding: 16,
};

const detailListStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 10,
  margin: '16px 0 0',
};
